import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminStudentsService } from '@services/admin-students/adminStudentsService';
import { ArrowLeft, User, Key, Award, CheckCircle, FileText, ExternalLink, FolderGit2 } from 'lucide-react';
import Button from '@components/Button/Button';
import ConfirmModal from '@components/common/ConfirmModal/ConfirmModal';
import StudentEvaluationCard from '@pages/EvaluationsPage/StudentEvaluationCard';
import toast from 'react-hot-toast';

const getFileUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api/v1').replace('/api/v1', '');
    return `${baseUrl}/${path.startsWith('/') ? path.slice(1) : path}`;
};

const AdminStudentDetails: React.FC = () => {
    const { t } = useTranslation('students');
    const { id } = useParams<{ id: string }>();
    const [student, setStudent] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [isCertModalOpen, setIsCertModalOpen] = useState(false);
    const [isDeleteLinkedInModalOpen, setIsDeleteLinkedInModalOpen] = useState(false);

    const fetchStudent = async () => {
        try {
            setLoading(true);
            const res = await adminStudentsService.getById(id!);
            setStudent(res.data.data);
        } catch (err: any) {
            setError(err.response?.data?.message || t('admin.details.notFound'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchStudent();
    }, [id]);

    const handleResetPassword = async () => {
        setIsResetModalOpen(false);
        try {
            setActionLoading(true);
            await adminStudentsService.resetPassword(id!);
            toast.success(t('admin.details.resetSuccess', { defaultValue: 'Password reset email sent successfully to the student.' }));
            fetchStudent(); // Refresh data to see force_password_change status
        } catch (err: any) {
            toast.error(err.response?.data?.message || t('admin.details.resetFail'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleGenerateCertificate = async () => {
        setIsCertModalOpen(false);
        try {
            setActionLoading(true);
            await adminStudentsService.generateCertificate(id!);
            toast.success(t('admin.details.certSuccess'));
            fetchStudent(); // Refresh data to see the new certificate
        } catch (err: any) {
            toast.error(err.response?.data?.message || t('admin.details.certFail'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleResetLinkedInContent = async () => {
        setIsDeleteLinkedInModalOpen(false);
        try {
            setActionLoading(true);
            await adminStudentsService.resetLinkedInContent(id!);
            toast.success('Generated LinkedIn post content removed successfully.');
            fetchStudent();
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to remove LinkedIn content.');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <div className="p-8 text-center">{t('admin.details.loading')}</div>;
    if (error || !student) return <div className="p-8 text-center text-red-500">{error || t('admin.details.notFound')}</div>;

    const { enrollment } = student;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <Link to="/admin/students" className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <ArrowLeft size={20} />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('admin.details.title')}</h1>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Card */}
                <div className="bg-white rounded-xl shadow-sm border border-border p-6 space-y-6">
                    <div className="flex items-center gap-4 border-b border-gray-100 pb-6">
                        <div className="w-16 h-16 bg-blue-100 text-blue-bg rounded-full flex items-center justify-center text-2xl font-bold uppercase">
                            {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{student.first_name} {student.last_name}</h2>
                            <p className="text-gray-500 text-sm">{student.email}</p>
                            <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${student.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {student.is_active ? t('admin.details.active') : t('admin.details.inactive')}
                            </span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <Key size={18} className="text-gray-400" />
                            {t('admin.details.security')}
                        </h3>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                            <div className="flex justify-between items-center mb-4">
                                <span className="text-sm text-gray-600">{t('admin.details.passwordStatus')}</span>
                                {student.force_password_change ? (
                                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded font-medium">{t('admin.details.pendingReset')}</span>
                                ) : (
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-medium">{t('admin.details.secure')}</span>
                                )}
                            </div>

                            <Button 
                                variant="outline" 
                                className="w-full text-sm" 
                                onClick={() => setIsResetModalOpen(true)}
                                isLoading={actionLoading}
                            >
                                {t('admin.details.resetPasswordBtn')}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Enrollment & Cohort Data */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-border p-6">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4 border-b border-gray-100 pb-4">
                            <User size={18} className="text-blue-bg" />
                            {t('admin.details.enrollmentInfo')}
                        </h3>
                        
                        {enrollment ? (
                            <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">{t('admin.details.enrollmentId')}</p>
                                    <p className="font-mono text-gray-900 font-medium bg-gray-50 inline-block px-2 py-1 rounded border border-gray-100">
                                        {enrollment.enrollment_number}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">{t('admin.details.assignedCohort')}</p>
                                    <p className="font-medium text-gray-900">{enrollment.cohort?.generated_cohort_title}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">{t('admin.details.enrollmentStatus')}</p>
                                    <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600">
                                        <CheckCircle size={16} /> {enrollment.status}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">{t('admin.details.noEnrollment')}</p>
                        )}
                    </div>

                    {/* Documents & Certificates */}
                    {enrollment && (
                        <div className="bg-white rounded-xl shadow-sm border border-border p-6">
                            <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-4">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <Award size={18} className="text-blue-bg" />
                                    {t('admin.details.documents')}
                                </h3>
                                
                                <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={() => setIsCertModalOpen(true)}
                                    isLoading={actionLoading}
                                >
                                    {enrollment.certificate ? 'Regenerate Certificate' : t('admin.details.generateCertBtn')}
                                </Button>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <FileText size={20} className="text-blue-500" />
                                        <span className="font-medium text-sm">{t('admin.details.enrollmentLor')}</span>
                                    </div>
                                    {enrollment.application?.lor_path ? (
                                        <a href={getFileUrl(enrollment.application.lor_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-semibold border border-blue-200 transition-colors shadow-sm">{t('admin.details.viewPdf')}</a>
                                    ) : (
                                        <span className="text-sm text-gray-400">{t('admin.details.notGenerated')}</span>
                                    )}
                                </div>

                                <div className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Award size={20} className={enrollment.certificate ? "text-yellow-500" : "text-gray-400"} />
                                        <div>
                                            <span className="font-medium text-sm block">{t('admin.details.completionCert')}</span>
                                            {enrollment.certificate && (
                                                <span className="text-xs text-gray-400">
                                                    Issued: {new Date(enrollment.certificate.completion_date || enrollment.certificate.issued_at).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {enrollment.certificate ? (
                                        <div className="flex items-center gap-2">
                                            <a 
                                                href={getFileUrl(enrollment.certificate.certificate_url)} 
                                                target="_blank" 
                                                rel="noreferrer" 
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-semibold border border-blue-200 transition-colors shadow-sm"
                                            >
                                                {t('admin.details.viewCert')}
                                            </a>
                                            <Link
                                                to={`/verify/${enrollment.certificate.id}`}
                                                target="_blank"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg text-xs font-semibold border border-emerald-200 transition-colors shadow-sm"
                                            >
                                                <span>Public Page</span>
                                                <ExternalLink size={12} />
                                            </Link>
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-400">{t('admin.details.notAwarded')}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Demo Day Evaluation */}
                    {enrollment && <StudentEvaluationCard enrollmentId={enrollment.id} />}

                    {/* Student Capstone Project Details */}
                    {enrollment?.application && (
                        <div className="bg-white rounded-xl shadow-sm border border-border p-6 space-y-4">
                            <h3 className="font-semibold text-gray-900 flex items-center gap-2 border-b border-gray-100 pb-4">
                                <FolderGit2 size={18} className="text-blue-bg" />
                                Capstone Project Information
                            </h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs font-semibold uppercase text-gray-400">Project Title</p>
                                    <p className="text-sm font-semibold text-gray-900 mt-0.5">
                                        {enrollment.application.project_title || <span className="text-gray-400 italic font-normal">Not provided yet</span>}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold uppercase text-gray-400">Project Description</p>
                                    <p className="text-xs text-gray-700 mt-0.5 bg-gray-50 p-3 rounded-lg border border-gray-100 whitespace-pre-line leading-relaxed">
                                        {enrollment.application.project_description || <span className="text-gray-400 italic font-normal">No description provided yet</span>}
                                    </p>
                                </div>
                                {enrollment.application.demo_presentation_path && (
                                    <div>
                                        <p className="text-xs font-semibold uppercase text-gray-400">Demo Presentation PDF</p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <a
                                                href={getFileUrl(enrollment.application.demo_presentation_path)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-semibold border border-blue-200 transition-colors shadow-sm"
                                            >
                                                <FileText size={14} />
                                                <span>{enrollment.application.demo_presentation_name || 'View Demo Presentation PDF'}</span>
                                                <ExternalLink size={12} />
                                            </a>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs font-semibold uppercase text-gray-400">Problem Area (AI Generated)</p>
                                    <p className="text-sm font-semibold text-gray-900 mt-0.5 inline-flex items-center gap-2">
                                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">
                                            {enrollment.application.problem_area || 'Not generated yet'}
                                        </span>
                                    </p>
                                </div>
                                <div className="pt-2 border-t border-gray-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-xs font-semibold uppercase text-gray-400">LinkedIn Post Content (AI Generated &amp; Cached)</p>
                                        {enrollment.application.linkedin_post_content && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-red-600 border-red-200 hover:bg-red-50 text-xs py-1 px-2.5"
                                                onClick={() => setIsDeleteLinkedInModalOpen(true)}
                                                isLoading={actionLoading}
                                            >
                                                Remove Generated Content
                                            </Button>
                                        )}
                                    </div>
                                    {enrollment.application.linkedin_post_content ? (
                                        <div className="bg-blue-50/40 p-3 rounded-lg border border-blue-100 text-xs text-gray-800 whitespace-pre-line leading-relaxed font-sans">
                                            {enrollment.application.linkedin_post_content}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-400 italic bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                                            No LinkedIn post content generated yet. It will generate when the student clicks Share to LinkedIn on their profile.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={isResetModalOpen}
                onClose={() => setIsResetModalOpen(false)}
                onConfirm={handleResetPassword}
                title={t('admin.details.resetPasswordBtn')}
                message={t('admin.details.resetConfirm')}
                confirmLabel="Reset"
                variant="danger"
                loading={actionLoading}
            />

            <ConfirmModal
                isOpen={isCertModalOpen}
                onClose={() => setIsCertModalOpen(false)}
                onConfirm={handleGenerateCertificate}
                title={enrollment?.certificate ? 'Regenerate Certificate' : t('admin.details.generateCertBtn')}
                message={enrollment?.certificate ? 'Are you sure you want to regenerate the certificate? A fresh certificate PDF will be generated and the existing record updated.' : t('admin.details.certConfirm')}
                confirmLabel={enrollment?.certificate ? 'Regenerate' : 'Generate'}
                variant="info"
                loading={actionLoading}
            />

            <ConfirmModal
                isOpen={isDeleteLinkedInModalOpen}
                onClose={() => setIsDeleteLinkedInModalOpen(false)}
                onConfirm={handleResetLinkedInContent}
                title="Remove Generated Content"
                message="Are you sure you want to delete the cached LinkedIn post content for this student? On the next test or when the student opens LinkedIn sharing, Gemini AI will generate fresh content."
                confirmLabel="Remove Content"
                variant="danger"
                loading={actionLoading}
            />
        </div>
    );
};

export default AdminStudentDetails;
